// import { GenericChipColor } from './chips';
import { GENERIC_CHIP_COLOR } from '@shared/types/enums';

export type WorkerId = string;

export type Worker = {
  id: WorkerId;
  data: Data;
  description: string;
  devices: Device[];
  isSubscription: boolean;
  isStaked?: boolean;
  name: string;
  namespace: string;
  staking?: Staking;
  status: Status;
  subscriptions: Subscription[];
  type: string;
  versions: Version[];
};

export type Data = {
  info: {
    description: string;
    logoUrl: string | null;
    name: string;
    publisherInfo: string;
  };
  namespace: string;
  operationEndBlock: string;
  operationStartBlock: string;
  operatorsConfig: {
    allowedOperators: string;
    maxOperatorWorkers: string;
    stakingAmounts: {
      max: string;
      min: string;
    };
    startBlock: string;
  };
  rewardsConfig: {
    activeParticipationAmount: string;
    minimumParticipationTime: string;
    subscriptionRewardAmount: string;
    topPerformanceBonus: string;
  };
};

type Device = {
  id: number;
  createdBy: string;
  creationDate: string;
  items: string;
  lastUpdateDate: string;
  lastUpdateBy: string;
  name: string;
  worker: WorkerId;
};

type Staking = {
  id: number;
  createdBy: string;
  creationDate: string;
  lastUpdateDate: string;
  lastUpdatedBy: string;
  lockupDuration: number;
  lockupDurationType: string;
  minLockupBalance: number;
  region: string;
  workers: WorkerId;
};

type Status = {
  createdDate: string | Date;
  downloadErrorStatus: string | null;
  downloadPath: string | null;
  downloadStatus: string | null;
  downloadVersion: string | null;
  downloadVersionDate: string | null | Date;
  installDate: string | null | Date;
  installErrorStatus: string | null;
  installStatus: string | null;
  installVersion: string | null;
  installVersionDate: string | null | Date;
  isFavourites: boolean;
  isInstall: boolean;
  isSubscription: boolean;
  namespace: string;
  noderedId: string | null;
  status: string | null;
  statusDate: string | null | Date;
  type: string | null;
  updatedDate: string | null | Date;
  workerId: string;
  workerName: string;
};

type Subscription = {
  createdBy: string;
  creationDate: string;
  description: string | null;
  frequency: string;
  id: number;
  lastUpdateDate: string;
  lastUpdatedBy: string;
  workers: WorkerId;
};

type Version = {
  changeLogs: string;
  createdBy: string;
  creationDate: string;
  flowSource: string;
  id: number;
  lastUpdatedBy: string;
  updatedDate: string;
  versionNo: string;
  worker: WorkerId;
};

export type CurrentVoteAndReward = {
  votes: number;
  rewardPeriod: number;
};

export type StakeGroupAmount = {
  groupId: string;
  amount: string;
  floatingAmount: string;
  isReadyToVote: boolean;
};

export type SolutionNodeStatus = {
  groupId: string;
  solutionName: string;
  solutionId: number;
  isActive: boolean;
  lastActive: Date | undefined;
  ruleActiveInMinute: number;
  noderedId: string;
};

export type SolutionRule = {
  groupId: string;
  solutionId: string;
  ruleActiveInMinute: number;
};

export type UnClaimRewards = {
  isDeleted: boolean;
  isExpired: boolean;
  isSubscribed: boolean;
  groupId: string;
  ewxAddress: string;
  totalReward: string;
  groupNamespace: string;
  groupName: string;
  groupDescription: string;
  groupPublisherInfo: string;
};

export type GeoLocation = {
  enabled: boolean;
  coord?: {
    latitude: number;
    longitude: number;
    timestamp: number;
  };
  registry?: {
    latitude: number;
    longitude: number;
    timestamp: number;
    rewardPeriod?: number;
  };
  updateRewardPeriod?: number;
  message?: string;
};

export enum WORKER_STATUS {
  Downloading = 'Downloading',
  Downloaded = 'Downloaded',
  DownloadFailed = 'Download Failed',
  Installing = 'Installing',
  Installed = 'Installed',
  InstallFailed = 'Install Failed',
  Pausing = 'Pausing',
  Paused = 'Paused',
  Resuming = 'Resuming',
  Terminating = 'Terminating',
  Terminated = 'Terminated',
  Running = 'Running',
  Expired = 'Expired',
  Scheduled = 'Scheduled',
  NoSolution = 'No Active Solutions',
  NotRunning = 'Not Running',
  AutoInstallationScheduled = 'Auto-Installation Scheduled',
}

export enum WorkerStatusColor {
  Downloading = GENERIC_CHIP_COLOR.Blue,
  Downloaded = GENERIC_CHIP_COLOR.Teal,
  'Download Failed' = GENERIC_CHIP_COLOR.Red,
  Installing = GENERIC_CHIP_COLOR.Blue,
  Installed = GENERIC_CHIP_COLOR.Teal,
  'Install Failed' = GENERIC_CHIP_COLOR.Red,
  Pausing = GENERIC_CHIP_COLOR.White,
  Paused = GENERIC_CHIP_COLOR.White,
  Resuming = GENERIC_CHIP_COLOR.Blue,
  Terminating = GENERIC_CHIP_COLOR.Red,
  Terminated = GENERIC_CHIP_COLOR.Gray,
  Running = GENERIC_CHIP_COLOR.Teal,
  Expired = GENERIC_CHIP_COLOR.Gray,
  Scheduled = GENERIC_CHIP_COLOR.LightPink,
  'No Active Solutions' = GENERIC_CHIP_COLOR.Gray,
  'Not Running' = GENERIC_CHIP_COLOR.Gray,
}

export enum ScheduleActionLabel {
  ForDownload = 'Downloading',
  ForInstall = 'Installing',
  ForTerminate = 'Terminating',
  ForResume = 'Resuming',
  ForPaused = 'Pausing',
  NoAction = 'NoAction',
}

export enum ManageWorkerType {
  Pause = 'Pause',
  Resume = 'Resume',
}
export type ManageWorker = ManageWorkerType.Pause | ManageWorkerType.Resume;
